import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Breadcrumb } from "../Breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { Check, X as RejectIcon, Trash2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import API from "../api/api01";

export interface Leave {
  id: string;
  employee: string;
  type: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: "pending" | "approved" | "rejected";
}

export function LeaveManagement() {

  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");

  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [confirmAction, setConfirmAction] = useState<"approve" | "reject" | null>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get("/superadmin/leaves/all");
      setLeaves(res.data);
      if (res.data && res.data.length > 0) {
        toast.success("Leave requests synchronized");
      } else {
        toast.error("No records found");
      }
    } catch (err) {
      toast.error("Failed to fetch leave requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  /* -------------------- FILTER -------------------- */

  const filteredLeaves =
    filter === "all" ? leaves : leaves.filter((l) => l.status === filter);

  /* -------------------- DURATION -------------------- */

  const getDuration = (start: string, end: string) => {
    const s = new Date(start);
    const e = new Date(end);
    const diff = (e.getTime() - s.getTime()) / (1000 * 3600 * 24) + 1;
    return `${diff} day${diff > 1 ? "s" : ""}`;
  };

  /* -------------------- AVATAR -------------------- */

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  /* -------------------- APPROVE / REJECT -------------------- */

  const openConfirmDialog = (leave: Leave, action: "approve" | "reject") => {
    setSelectedLeave(leave);
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {

    if (!selectedLeave || !confirmAction) return;

    try {
      const status = confirmAction === "approve" ? "Approved" : "Rejected";
      await API.put(`/superadmin/leaves/${selectedLeave.id}/status`, { status });
      
      setLeaves(leaves.map((l) =>
        l.id === selectedLeave.id ? { ...l, status: status.toLowerCase() as any } : l
      ));

      if (confirmAction === "approve") {
        toast.success(`Leave approved for ${selectedLeave.employee}`);
      } else {
        toast.error(`Leave rejected for ${selectedLeave.employee}`);
      }
    } catch (err) {
      toast.error("Failed to update leave status");
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  /* -------------------- DELETE -------------------- */

  const openDeleteDialog = (leave: Leave) => {
    setSelectedLeave(leave);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedLeave) return;

    try {
      await API.delete(`/superadmin/leaves/${selectedLeave.id}`);
      setLeaves(leaves.filter((l) => l.id !== selectedLeave.id));
      toast.success("Leave request deleted");
    } catch (err) {
      toast.error("Failed to delete leave request");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const statusStyles = {
    approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  };

  return (

    <div className="min-h-screen bg-background p-6 transition-colors duration-500">

      <TooltipProvider>

        <div className="max-w-7xl mx-auto space-y-6">

          {/* HEADER */}

          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
              Leave Management
            </h1>

            <Breadcrumb items={[{ label: "Leave Management" }]} className="mt-2" />
          </div>



          {/* FILTER BOX */}

          <div className="bg-card backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-sm">

            <div className="flex gap-3 flex-wrap">

              {/* ALL */}

              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest border border-slate-300 dark:border-slate-800 transition-all duration-300
      ${filter === "all"
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20"
                    : "bg-white/80 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800"
                   }`}
              >
                All
              </button>


              {/* PENDING */}

              <button
                onClick={() => setFilter("pending")}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest border border-slate-300 dark:border-slate-800 transition-all duration-300
      ${filter === "pending"
                    ? "bg-amber-500 text-white shadow-lg shadow-amber-500/20"
                    : "bg-white/80 dark:bg-slate-900/50 text-amber-600 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800"
                   }`}
              >
                Pending
              </button>


              {/* APPROVED */}

              <button
                onClick={() => setFilter("approved")}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest border border-slate-300 dark:border-slate-800 transition-all duration-300
      ${filter === "approved"
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                    : "bg-white/80 dark:bg-slate-900/50 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-800"
                   }`}
              >
                Approved
              </button>


              {/* REJECTED */}

              <button
                onClick={() => setFilter("rejected")}
                className={`px-4 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest border border-slate-300 dark:border-slate-800 transition-all duration-300
      ${filter === "rejected"
                    ? "bg-red-500 text-white shadow-lg shadow-red-500/20"
                    : "bg-white/80 dark:bg-slate-900/50 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800"
                   }`}
              >
                Rejected
              </button>


            </div>

          </div>



          {/* TABLE */}

          <Card className="bg-card backdrop-blur-xl border border-white/10 shadow-xl overflow-hidden rounded-2xl">

            <CardHeader className="border-b border-white/10 pb-3">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">Leave Requests ({filteredLeaves.length})</CardTitle>
            </CardHeader>


            <CardContent className="p-0">

            {loading ? (
              <div className="h-64 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <p>Loading leave requests...</p>
              </div>
            ) : (
              <Table>

                  <TableHeader>
                    <TableRow className="bg-background/50 border-b border-white/5">
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">ID</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Employee</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Type</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Dates</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Duration</TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3">Status</TableHead>
                      <TableHead className="text-right text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 pr-4">Actions</TableHead>
                    </TableRow>
                  </TableHeader>


                <TableBody>

                  {filteredLeaves.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="items-center py-20 text-muted-foreground group">
                        <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in-95 duration-500">
                          <div className="h-20 w-20 rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform shadow-inner">
                            <Clock className="h-10 w-10 opacity-40" />
                          </div>
                          <div className="space-y-2 text-center">
                            <p className="text-xl font-bold text-slate-900 dark:text-white">No leave records</p>
                            <p className="text-sm font-medium leading-relaxed max-w-[280px]">When team members submit time-off requests, they will appear here for your review and approval.</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredLeaves.map((leave) => (

                    <TableRow
                      key={leave.id}
                      className="relative border-b border-white/5 hover:bg-background/80 transition-all duration-200"
                    >

                      {/* STATUS BAR */}

                      <div
                        className={`absolute left-0 top-0 h-full w-1 ${leave.status === "approved"
                            ? "bg-emerald-500"
                            : leave.status === "rejected"
                              ? "bg-red-500"
                              : "bg-amber-500"
                          }`}
                      />

                      <TableCell>{leave.id}</TableCell>

                      {/* EMPLOYEE */}

                      <TableCell>

                        <div className="flex items-center gap-3">

                          <div className="h-9 w-9 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-sm font-bold text-blue-600 dark:text-blue-400">
                            {getInitials(leave.employee)}
                          </div>

                          <span className="text-[13px] font-bold text-slate-900 dark:text-white">{leave.employee}</span>


                        </div>

                      </TableCell>

                      <TableCell>{leave.type}</TableCell>

                      <TableCell className="text-[13px] font-mono text-slate-500">
                        {leave.startDate} ΓåÆ {leave.endDate}
                      </TableCell>


                      {/* DURATION */}

                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] font-bold uppercase tracking-widest border-none">
                          {getDuration(leave.startDate, leave.endDate)}
                        </Badge>
                      </TableCell>


                      {/* STATUS */}

                      <TableCell>
                        <Badge className={`${statusStyles[leave.status]} text-[10px] font-bold uppercase tracking-widest border-none`}>
                          {leave.status}
                        </Badge>
                      </TableCell>


                      {/* ACTIONS */}

                      <TableCell className="text-right">

                        <div className="flex justify-end gap-1">

                          {leave.status === "pending" && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openConfirmDialog(leave, "approve")}
                                  >
                                    <Check className="h-4 w-4 text-emerald-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Approve</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openConfirmDialog(leave, "reject")}
                                  >
                                    <RejectIcon className="h-4 w-4 text-red-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Reject</TooltipContent>
                              </Tooltip>
                            </>
                          )}

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => openDeleteDialog(leave)}
                              >
                                <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500 transition-colors" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>

                        </div>

                      </TableCell>

                    </TableRow>

                  ))}

                </TableBody>

              </Table>
            )}

            </CardContent>

          </Card>

          {/* CONFIRM DIALOG */}

          <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
            <AlertDialogContent className="bg-card border border-white/10 shadow-2xl rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Action</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to {confirmAction} leave for{" "}
                  <strong>{selectedLeave?.employee}</strong>?
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmAction}
                  className={
                    confirmAction === "approve"
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"
                  }
                >
                  Confirm
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* DELETE DIALOG */}

          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Leave</AlertDialogTitle>
                <AlertDialogDescription>
                  Delete leave request for{" "}
                  <strong>{selectedLeave?.employee}</strong>?
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </div>

      </TooltipProvider>

    </div>
  );
}
